import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

export default function CompanyDeleteConfirmModal({
  company,
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  cascadeInfo = null
}) {
  const [confirmationText, setConfirmationText] = useState('');
  const [isConfirmationValid, setIsConfirmationValid] = useState(false);

  useEffect(() => {
    if (company && confirmationText === company.name) {
      setIsConfirmationValid(true);
    } else {
      setIsConfirmationValid(false);
    }
  }, [confirmationText, company]);

  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setIsConfirmationValid(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!isConfirmationValid) {
      toast.error('Please type the company name to confirm deletion');
      return;
    }

    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Failed to delete company');
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    setIsConfirmationValid(false);
    onClose();
  };

  if (!isOpen || !company) return null;

  const hasImpact = cascadeInfo && (cascadeInfo.userCount > 0 || cascadeInfo.jobCount > 0 || cascadeInfo.contactCount > 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Delete Company
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{company.name}</span>? 
                  This action cannot be undone.
                </p>

                {/* Company Info */}
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-3">
                    {company.logo ? (
                      <img 
                        className="h-8 w-8 rounded-full object-cover" 
                        src={company.logo} 
                        alt={`${company.name} logo`}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-600">
                          {company.name?.charAt(0)?.toUpperCase() || 'C'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{company.name}</p>
                      <p className="text-xs text-gray-500">{company.industry}</p>
                    </div>
                  </div>
                </div>

                {/* Cascade Impact Warning */}
                {hasImpact && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-800">
                          Warning: This will affect related data
                        </h4>
                        <div className="mt-2 text-sm text-yellow-700">
                          <ul className="list-disc list-inside space-y-1">
                            {cascadeInfo.userCount > 0 && (
                              <li>{cascadeInfo.userCount} user{cascadeInfo.userCount !== 1 ? 's' : ''} associated with this company</li>
                            )}
                            {cascadeInfo.jobCount > 0 && (
                              <li>{cascadeInfo.jobCount} job posting{cascadeInfo.jobCount !== 1 ? 's' : ''} from this company</li>
                            )}
                            {cascadeInfo.contactCount > 0 && (
                              <li>{cascadeInfo.contactCount} contact{cascadeInfo.contactCount !== 1 ? 's' : ''} linked to this company</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Confirmation Input */}
                <div className="mt-4">
                  <label htmlFor="confirmationText" className="block text-sm font-medium text-gray-700">
                    Type <span className="font-semibold">{company.name}</span> to confirm deletion:
                  </label>
                  <input
                    type="text"
                    id="confirmationText"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    placeholder={company.name}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !isConfirmationValid}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete Company'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}